/**
 * linalg.ts — minimal typed-array linear algebra.
 *
 * Copied verbatim from fishing-demo (plan §6: "linalg.ts — copied verbatim from
 * fishing-demo"). Not all helpers are used by the vacuum agent (Cholesky and
 * quadraticForm are bandit-specific), but keeping the file identical means the
 * two demos share one well-tested linalg surface and there is nothing to
 * re-audit here.
 *
 * Everything works on flat `Float64Array`s. We never build nested arrays: a
 * matrix is a flat buffer plus a stride (column count). Float64 (not Float32)
 * — the Q-net's backward pass accumulates error otherwise.
 */

/** v^T w  — dot product of two equal-length vectors. */
export function dot(v: Float64Array, w: Float64Array): number {
  let s = 0;
  const n = v.length;
  for (let i = 0; i < n; i++) s += v[i] * w[i];
  return s;
}

/** L2 norm ‖v‖. */
export function norm(v: Float64Array): number {
  return Math.sqrt(dot(v, v));
}

/**
 * Logistic sigmoid, numerically stable. Two branches keyed on the sign of z:
 *   z ≥ 0 : 1 / (1 + e^-z)         (no overflow: e^-z ≤ 1)
 *   z < 0 : e^z / (1 + e^z)        (no overflow: e^z < 1)
 */
export function sigmoid(z: number): number {
  if (z >= 0) return 1 / (1 + Math.exp(-z));
  const e = Math.exp(z);
  return e / (1 + e);
}

/**
 * Solve A x = b in place via Cholesky factorisation. A must be symmetric
 * positive definite — which the GLM-UCB Hessian is, thanks to the `+ λI`
 * ridge term. (Bandit-only; the vacuum agent does not use it.)
 *
 * A is a d×d flat row-major buffer; b is length d. Returns a new Float64Array.
 * Throws if A is not positive definite — caller should fall back gracefully.
 */
export function choleskySolve(A: Float64Array, b: Float64Array, d: number): Float64Array {
  // L = Cholesky factor, lower-triangular, A = L L^T. Compute in place into L.
  const L = new Float64Array(d * d);
  for (let i = 0; i < d; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = A[i * d + j];
      for (let k = 0; k < j; k++) sum -= L[i * d + k] * L[j * d + k];
      if (i === j) {
        if (sum <= 1e-12) {
          throw new Error(`Cholesky failed: non-positive pivot ${sum} at (${i},${j})`);
        }
        L[i * d + j] = Math.sqrt(sum);
      } else {
        L[i * d + j] = sum / L[j * d + j];
      }
    }
  }
  // Solve L y = b (forward substitution), then L^T x = y (back substitution).
  const y = new Float64Array(d);
  for (let i = 0; i < d; i++) {
    let sum = b[i];
    for (let k = 0; k < i; k++) sum -= L[i * d + k] * y[k];
    y[i] = sum / L[i * d + i];
  }
  const x = new Float64Array(d);
  for (let i = d - 1; i >= 0; i--) {
    let sum = y[i];
    for (let k = i + 1; k < d; k++) sum -= L[k * d + i] * x[k];
    x[i] = sum / L[i * d + i];
  }
  return x;
}

/**
 * x^T A x for a single vector x and a d×d matrix A (flat row-major).
 * (Bandit-only; the vacuum agent does not use it.)
 */
export function quadraticForm(x: Float64Array, A: Float64Array, d: number): number {
  // (A x)^T x — one matvec, one dot. Cheaper than forming x^T A first.
  const Ax = new Float64Array(d);
  for (let i = 0; i < d; i++) {
    let s = 0;
    for (let j = 0; j < d; j++) s += A[i * d + j] * x[j];
    Ax[i] = s;
  }
  return dot(x, Ax);
}

/**
 * argmax over a plain array. Returns the index of the first maximum.
 * Used to pick the highest-Q action and the highest-scoring frontier.
 */
export function argmax(scores: number[]): number {
  let bestIdx = 0;
  let best = -Infinity;
  for (let i = 0; i < scores.length; i++) {
    if (scores[i] > best) {
      best = scores[i];
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Mulberry32 — tiny fast PRNG with a seed. Deterministic for reproducible demos. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Standard normal via Box–Muller, drawing from a uniform PRNG. Used by the
 *  Q-net's He/Xavier initialisation. */
export function randn(rng: () => number): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
